import { Injectable } from '@angular/core';

export interface JwtPayload {
  exp: number;
  nbf?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  parseJwtToken(token: string): JwtPayload | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if necessary
      const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      // Decode base64 to string
      let decodedString: string;
      try {
        decodedString = atob(paddedBase64);
      } catch (error) {
        // Invalid base64
        return null;
      }
      
      // Parse the JSON payload
      try {
        return JSON.parse(decodedString);
      } catch (error) {
        // Invalid JSON
        return null;
      }
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  getTokenExpirationTime(token: string): number | null {
    const payload = this.parseJwtToken(token);
    if (payload && payload.exp) {
      // Convert from seconds to milliseconds
      return payload.exp * 1000;
    }
    return null;
  }

  isTokenExpired(token: string): boolean {
    const expirationTime = this.getTokenExpirationTime(token);
    if (!expirationTime) {
      return true;
    }
    
    const now = Date.now();
    return now >= expirationTime;
  }

  getRemainingTime(token: string): number {
    const expirationTime = this.getTokenExpirationTime(token);
    if (!expirationTime) {
      return 0;
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    return Math.max(0, Math.floor(timeUntilExpiration / 60000));
  }

  getTokenPayload(token: string): JwtPayload | null {
    return this.parseJwtToken(token);
  }

  hasClaim(token: string, claim: string): boolean {
    const payload = this.parseJwtToken(token);
    return payload ? !!payload[claim] : false;
  }

  getClaim(token: string, claim: string): any {
    const payload = this.parseJwtToken(token);
    if (!payload) {
      return null;
    }
    return payload.hasOwnProperty(claim) ? payload[claim] : null;
  }
}
